import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080/api';
const INGESTION_API_BASE_URL =
  (import.meta as any).env?.VITE_INGESTION_API_URL || API_BASE_URL;

// ─── 429 back-off state ────────────────────────────────────────────────────────
// When the server returns 429 we record the earliest time we are allowed to
// retry.  All subsequent requests are held until that time has elapsed, so
// the user can keep navigating without hammering the server during a cool-down.

let rateLimitedUntil = 0;          // epoch ms — 0 means "not rate limited"
let rateLimitListeners: Array<() => void> = []; // callbacks notified on change

/** Returns the remaining cool-down in ms (0 if not limited). */
export function getRateLimitRemainingMs(): number {
  return Math.max(0, rateLimitedUntil - Date.now());
}

/** Subscribe to changes in the rate-limit state (for UI banners). */
export function onRateLimitChange(cb: () => void): () => void {
  rateLimitListeners.push(cb);
  return () => { rateLimitListeners = rateLimitListeners.filter(l => l !== cb); };
}

function notifyRateLimitListeners() {
  rateLimitListeners.forEach(cb => cb());
}

// ─── Create axios instance ─────────────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and organization context
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // If we are currently rate-limited, wait until the cool-down expires
    const wait = getRateLimitRemainingMs();
    if (wait > 0) {
      await new Promise(resolve => setTimeout(resolve, wait));
    }

    const { token, currentOrganizationId } = useAuthStore.getState();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization context header for platform admins
    // This allows them to operate in the context of any organization
    if (currentOrganizationId) {
      config.headers['x-organization-context'] = currentOrganizationId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
    }

    if (error.response?.status === 429) {
      // Parse Retry-After header (seconds) sent by express-rate-limit standardHeaders
      const retryAfterSec = parseInt(
        (error.response.headers as Record<string, string>)['retry-after'] ?? '60',
        10,
      );
      const coolDownMs = (isNaN(retryAfterSec) ? 60 : retryAfterSec) * 1000;
      rateLimitedUntil = Date.now() + coolDownMs;
      notifyRateLimitListeners();

      // Auto-clear the flag once the window has passed
      setTimeout(() => {
        rateLimitedUntil = 0;
        notifyRateLimitListeners();
      }, coolDownMs);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: async (data: { email: string; password: string; name: string; organization?: string }) => {
    // Map frontend field names to backend field names
    const payload = {
      email: data.email,
      password: data.password,
      fullName: data.name,
      organizationName: data.organization || 'Default Organization',
    };
    const response = await api.post('/auth/register', payload);
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (data: { name?: string; organization?: string }) => {
    const response = await api.put('/auth/me', data);
    return response.data;
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

// Missions API
export const missionsApi = {
  getAll: async (params?: { page?: number; limit?: number; status?: string; crop_type?: string }) => {
    const response = await api.get('/missions', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/missions/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/missions', data);
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/missions/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/missions/${id}`);
    return response.data;
  },

  uploadImages: async (id: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const response = await api.post(`/missions/${id}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getImages: async (id: string) => {
    const response = await api.get(`/missions/${id}/images`);
    return response.data;
  },

  deleteImage: async (missionId: string, imageId: string) => {
    const response = await api.delete(`/missions/${missionId}/images/${imageId}`);
    return response.data;
  },

  getAnalysis: async (id: string) => {
    const response = await api.get(`/missions/${id}/analysis`);
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await api.get(`/missions/${id}/stats`);
    return response.data;
  },

  complete: async (id: string) => {
    const response = await api.post(`/missions/${id}/complete`);
    return response.data;
  },

  getAllStats: async () => {
    const response = await api.get('/missions/stats');
    return response.data;
  },
  createIngestionJob: async (
    id: string,
    data: {
      upload_session_ids: string[];
      hardware_metadata?: {
        drone_serial_number?: string;
        sensor_calibration_date?: string;
        pilot_id?: string;
      };
      pipeline_mode?:     string;
      processing_engine?: string;
      raw_format?:        string;
    }
  ) => {
    const response = await ingestionApi.post(`/missions/${id}/ingestion-jobs`, data);
    return response.data;
  },

  getIngestionJobs: async (id: string) => {
    const response = await ingestionApi.get(`/missions/${id}/ingestion-jobs`);
    return response.data;
  },

  getIngestionJob: async (jobId: string) => {
    const response = await ingestionApi.get(`/missions/ingestion-jobs/${jobId}`);
    return response.data;
  },
};

// Analysis API
export const analysisApi = {
  analyzeImage: async (data: { image_id: string; analysis_type?: string; prompt?: string }) => {
    const response = await api.post('/analysis/image', data);
    return response.data;
  },

  uploadAndAnalyze: async (file: File, missionId: string, analysisType?: string, prompt?: string) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('mission_id', missionId);
    if (analysisType) formData.append('analysis_type', analysisType);
    if (prompt) formData.append('prompt', prompt);

    const response = await api.post('/analysis/image/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  processNLQ: async (data: { query: string; mission_id?: string; context?: any }) => {
    const response = await api.post('/analysis/nlq', data);
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/analysis/${id}`);
    return response.data;
  },

  getAll: async (params?: { page?: number; limit?: number; mission_id?: string; analysis_type?: string }) => {
    const response = await api.get('/analysis', { params });
    return response.data;
  },

  submitFeedback: async (id: string, data: { rating: number; feedback?: string }) => {
    const response = await api.post(`/analysis/${id}/feedback`, data);
    return response.data;
  },

  searchSimilar: async (query: string, limit?: number) => {
    const response = await api.get('/analysis/search/similar', { params: { query, limit } });
    return response.data;
  },

  batchAnalyze: async (data: { image_ids: string[]; analysis_type?: string; prompt?: string }) => {
    const response = await api.post('/analysis/batch', data);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/analysis/stats/summary');
    return response.data;
  },

  getMissionBriefing: async (data: {
    anomalyType: string;
    severity: string;
    changePercentage: number;
    affectedAreaHectares: number;
    boundaryName: string;
    detectionDate: string;
    description?: string;
    plan: {
      primaryPayload: string;
      secondaryPayload: string | null;
      altitudeM: number;
      overlapPercent: number;
      gridPattern: string;
      estimatedFlightMin: number;
      weatherConstraints: string[];
    };
  }) => {
    const response = await api.post('/analysis/mission-briefing', data);
    return response.data as {
      success: boolean;
      data: {
        briefing: {
          rationale: string;
          pilotBriefing: string;
          droneRecommendation: string;
        };
      };
    };
  },

  getWeatherCheck: async (data: {
    latitude: number;
    longitude: number;
    flightDate: string;
    weatherConstraints: string[];
  }) => {
    const response = await api.post('/analysis/weather-check', data);
    return response.data as {
      success: boolean;
      data: {
        weather: {
          verdict: 'go' | 'caution' | 'no-go';
          summary: string;
          recommendation: string;
          forecast: {
            date: string;
            maxWindSpeed_ms: number;
            precipitationMm: number;
            cloudCoverPct: number;
            tempMax_c: number;
            tempMin_c: number;
            uvIndex: number;
          };
        };
      };
    };
  },
};

// Satellite Boundaries API
export const satelliteBoundariesApi = {
  getAll: async (params?: { monitoring_enabled?: boolean; tags?: string; search?: string }) => {
    const response = await api.get('/satellite/boundaries', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/satellite/boundaries/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/satellite/boundaries/stats');
    return response.data;
  },
  create: async (data: {
    name: string;
    description?: string;
    geometry: { type: 'Polygon'; coordinates: number[][][] };
    monitoring_enabled?: boolean;
    monitoring_frequency?: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
  }) => {
    const response = await api.post('/satellite/boundaries', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    description?: string;
    geometry?: { type: 'Polygon'; coordinates: number[][][] };
    monitoring_enabled?: boolean;
    monitoring_frequency?: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
  }) => {
    const response = await api.put(`/satellite/boundaries/${id}`, data);
    return response.data;
  },
  deleteById: async (id: string) => {
    const response = await api.delete(`/satellite/boundaries/${id}`);
    return response.data;
  },
  importGeoJSON: async (geoJSON: unknown) => {
    const response = await api.post('/satellite/boundaries/import', { geoJSON });
    return response.data;
  },
  exportGeoJSON: async (boundaryIds?: string[]) => {
    const params = boundaryIds?.length ? { boundary_ids: boundaryIds } : undefined;
    const response = await api.get('/satellite/boundaries/export', { params });
    return response.data;
  },
};

// Satellite Imagery API (GEE)
export const satelliteImageryApi = {
  fetchImagery: async (
    boundaryId: string,
    params: { startDate: string; endDate: string; maxCloudCover?: number }
  ) => {
    const response = await api.post(`/satellite-imagery/${boundaryId}/fetch`, {}, { params });
    return response.data;
  },
  getImagery: async (
    boundaryId: string,
    params?: { startDate?: string; endDate?: string; limit?: number }
  ) => {
    const response = await api.get(`/satellite-imagery/${boundaryId}`, { params });
    return response.data;
  },
  getTimeSeries: async (
    boundaryId: string,
    params?: { indexType?: string; startDate?: string; endDate?: string }
  ) => {
    const response = await api.get(`/satellite-imagery/${boundaryId}/time-series`, { params });
    return response.data;
  },
  getThumbnail: async (
    imageryId: string,
    params?: { bands?: string; min?: number; max?: number }
  ) => {
    const response = await api.get(`/satellite-imagery/${imageryId}/thumbnail`, { params });
    return response.data;
  },
  deleteImagery: async (imageryId: string) => {
    const response = await api.delete(`/satellite-imagery/${imageryId}`);
    return response.data;
  },
};

// Anomaly Detection API (GEE change detection)
export const anomalyApi = {
  detectChanges: async (boundaryId: string) => {
    const response = await api.post(`/anomalies/${boundaryId}/detect`);
    return response.data;
  },
  runMonitoring: async (vertical?: string) => {
    const response = await api.post('/anomalies/monitor', undefined, { params: vertical ? { vertical } : undefined });
    return response.data;
  },
  getAnomalies: async (params?: {
    boundaryId?: string;
    severity?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    vertical?: string;
  }) => {
    const response = await api.get('/anomalies', { params });
    return response.data;
  },
  getStats: async (vertical?: string) => {
    const response = await api.get('/anomalies/stats', { params: vertical ? { vertical } : undefined });
    return response.data;
  },
  getById: async (anomalyId: string) => {
    const response = await api.get(`/anomalies/${anomalyId}`);
    return response.data;
  },
  updateStatus: async (
    anomalyId: string,
    data: { status: 'pending' | 'investigating' | 'resolved' | 'false_positive'; notes?: string }
  ) => {
    const response = await api.patch(`/anomalies/${anomalyId}/status`, data);
    return response.data;
  },
  /** Step 4 — record an immutable human verification decision */
  verify: async (
    anomalyId: string,
    data: {
      action: 'confirmed_drone' | 'confirmed_ground' | 'rejected' | 'escalated' | 'deferred';
      notes?: string;
      rejection_reason?: string;
    }
  ) => {
    const response = await api.post(`/anomalies/${anomalyId}/verify`, data);
    return response.data;
  },
  /** Step 4 — fetch the full verification audit trail */
  getVerifications: async (anomalyId: string) => {
    const response = await api.get(`/anomalies/${anomalyId}/verifications`);
    return response.data;
  },
  deleteAnomaly: async (anomalyId: string) => {
    const response = await api.delete(`/anomalies/${anomalyId}`);
    return response.data;
  },
};
// Notifications API
export const notificationsApi = {
  getAll: async (params?: { unreadOnly?: boolean; limit?: number }) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread/count');
    return response.data;
  },
  markRead: async (notificationId: string) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
};

// Organizations API (org-admin self-service)
export const organizationsApi = {
  getMyOrg: async (orgId: string) => {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
  },
  updateSettings: async (orgId: string, patch: Record<string, unknown>) => {
    const response = await api.patch(`/organizations/${orgId}/settings`, patch);
    return response.data;
  },
};

// Help Chat API
export const helpChatApi = {
  chat: async (data: {
    message: string;
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string; timestamp: string }>;
    relevantContext?: string;
  }) => {
    const response = await api.post('/help/chat', data);
    return response.data;
  },

  getSuggestions: async (query: string) => {
    const response = await api.post('/help/suggestions', { query });
    return response.data;
  },
};

export const gisPlatformApi = {
  getStatus: async () => {
    const response = await api.get('/gis-platform/status');
    return response.data;
  },

  getLaunchUrl: async (params?: { missionId?: string; returnUrl?: string }) => {
    const response = await api.get('/gis-platform/launch', { params });
    return response.data;
  },
};

export const ingestionApi: AxiosInstance = axios.create({
  baseURL: INGESTION_API_BASE_URL,
});

ingestionApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const wait = getRateLimitRemainingMs();
    if (wait > 0) {
      await new Promise(resolve => setTimeout(resolve, wait));
    }

    const { token, currentOrganizationId } = useAuthStore.getState();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (currentOrganizationId) {
      config.headers['x-organization-context'] = currentOrganizationId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

ingestionApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
    }

    if (error.response?.status === 429) {
      const retryAfterSec = parseInt(
        (error.response.headers as Record<string, string>)['retry-after'] ?? '60',
        10,
      );
      const coolDownMs = (isNaN(retryAfterSec) ? 60 : retryAfterSec) * 1000;
      rateLimitedUntil = Date.now() + coolDownMs;
      notifyRateLimitListeners();

      setTimeout(() => {
        rateLimitedUntil = 0;
        notifyRateLimitListeners();
      }, coolDownMs);
    }

    return Promise.reject(error);
  }
);

// ─── Conversation API (Step 9 — Ask Nabhos) ──────────────────────────────────
export const conversationApi = {
  ask: async (query: string, sessionId?: string | null) => {
    const response = await api.post('/conversation/ask', { query, sessionId: sessionId ?? null });
    return response.data as {
      success: boolean;
      data: {
        narrative: string;
        mapHighlight: { boundaryId: string; boundaryName: string; anomalyIds: string[]; severity: string | null } | null;
        dataTable: { date: string; anomalyType?: string; severity?: string; changePercent?: string | number; status?: string; affectedHectares?: number | null }[];
        suggestedActions: string[];
        sessionId: string;
        turnId: string;
      };
    };
  },

  listSessions: async () => {
    const response = await api.get('/conversation/sessions');
    return response.data as { success: boolean; data: { id: string; title: string; boundary_id: string | null; updated_at: string }[] };
  },

  getSessionTurns: async (sessionId: string) => {
    const response = await api.get(`/conversation/sessions/${sessionId}/turns`);
    return response.data as { success: boolean; data: { id: string; turn_index: number; user_query: string; response: any; created_at: string }[] };
  },
};

// ─── AgroTech Drone Operations API (Steps 5–8) ───────────────────────────────
export const agrotechDroneApi = {
  generateWaypoints: async (data: { coordinates: number[][]; altitudeM?: number; overlapPct?: number }) => {
    const response = await api.post('/missions/generate-waypoints', data);
    return response.data;
  },

  analyzeDroneFlight: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/analyze-agrotech`);
    return response.data;
  },

  verifyDroneFindings: async (missionId: string, data: {
    decision: 'confirm' | 'override' | 'additional_survey' | 'no_action';
    overrideReason?: string;
    overrideDiagnosis?: string;
    notes?: string;
  }) => {
    const response = await api.post(`/missions/${missionId}/verify-drone`, data);
    return response.data;
  },

  getRemediationPlan: async (missionId: string, remediationType?: string) => {
    const response = await api.post(`/missions/${missionId}/remediation-plan`, { remediationType });
    return response.data;
  },
};

// ─── Certified PDF Reports API (P2) ──────────────────────────────────────────
export const reportsApi = {
  downloadCertifiedPdf: async (type: 'mission_summary' | 'anomaly_evidence' | 'remediation_record' | 'carbon_credit', id: string) => {
    const response = await api.get(`/reports/${type}/${id}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nabhos_${type}_${id.slice(0, 8)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

// ─── Linear Assets API (P3) ──────────────────────────────────────────────────
export const linearAssetsApi = {
  getEncroachmentProjections: async () => {
    const response = await api.get('/linear-assets/encroachment-projections');
    return response.data;
  },

  getTrackObstructions: async () => {
    const response = await api.get('/linear-assets/track-obstructions');
    return response.data;
  },
};

export { API_BASE_URL, INGESTION_API_BASE_URL };

// ─── Demo Setup API (Platform Admin) ─────────────────────────────────────────

export interface DemoSeedFile {
  filename: string;
  label: string;
  description: string;
  vertical: 'base' | 'agrotech' | 'oil_gas' | 'platform';
  order: number;
  applied: boolean;
  fileExists: boolean;
}

export interface DemoSampleFile {
  filename: string;
  vertical: 'oil-gas' | 'agrotech';
  dataType: string;
  description: string;
  sizeLabel: string;
  available: boolean;
}

export interface DemoOrg {
  id: string;
  name: string;
  enabled_modules: string[];
}

export const demoApi = {
  getStatus: async (orgId?: string): Promise<{ seeds: DemoSeedFile[]; sampleFiles: DemoSampleFile[] }> => {
    const response = await api.get('/platform/demo/status', { params: orgId ? { orgId } : {} });
    return response.data.data;
  },

  getOrganisations: async (): Promise<DemoOrg[]> => {
    const response = await api.get('/platform/demo/organisations');
    return response.data.data;
  },

  runSeed: async (files: string[], orgId?: string): Promise<{ results: { filename: string; status: 'ok' | 'error'; error?: string }[] }> => {
    const response = await api.post('/platform/demo/seed', { files, ...(orgId ? { orgId } : {}) });
    return response.data.data;
  },

  reset: async (orgId?: string): Promise<{ message: string }> => {
    const response = await api.post('/platform/demo/reset', orgId ? { orgId } : {});
    return response.data.data;
  },

  /** Triggers a browser download for a single sample file */
  downloadFile: (vertical: 'oil-gas' | 'agrotech', filename: string): void => {
    const { token } = useAuthStore.getState();
    // Build a temporary anchor pointing to the API — browser handles download
    const url = `${API_BASE_URL}/platform/demo/files/${vertical}/${encodeURIComponent(filename)}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    // Attach auth token via a one-time fetch-blob approach for security
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      });
  },

  /** Triggers a browser download for the full vertical ZIP */
  downloadZip: (vertical: 'oil-gas' | 'agrotech'): void => {
    const { token } = useAuthStore.getState();
    const url = `${API_BASE_URL}/platform/demo/files/${vertical}/zip`;
    const filename = `nabhos-demo-${vertical}-files.zip`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      });
  },
};

export default api;

// Made with Bob
